export type LevelName = "Lv1" | "Lv2" | "Lv3" | "Lv4" | "Lv5" | "Lv6";
export type LevelKey = string;
export type LevelKind = "fact" | "measurement" | "method" | "reason" | "goal" | "commitment";
export type ScoringPolicy = "single" | "perItem" | "anyOf";

export const LEVEL_NAMES: LevelName[] = ["Lv1", "Lv2", "Lv3", "Lv4", "Lv5", "Lv6"];

export interface ConditionCategoryItem {
  categoryId: string;
  categoryName: string;
  level: LevelKey;
  kind?: LevelKind;
  itemKey: string;
  columnKey: string;
  sourceHeader: string;
  dimension: string;
  label: string;
  text: string;
  order: number;
  required: boolean;
  weight: number;
  scoringPolicy: ScoringPolicy;
  aliases: string[];
  reviewRequired: boolean;
  groupKey?: string;
  pairKey?: string;
  pairedItemKey?: string;
  levelOrder?: number;
}

export interface NormalizedConditionCategory {
  categoryId: string;
  categoryName: string;
  items: ConditionCategoryItem[];
  notes?: string;
}

export type LegacyCategoryTable = Record<string, Record<string, string>>;

type RawRecord = Record<string, unknown>;

const LEVEL_HEADER_RE = /^(Lv\d+)(?:_(.+))?$/;

const KIND_SORT_ORDER: Record<LevelKind, number> = {
  fact: 1,
  measurement: 2,
  method: 3,
  reason: 4,
  goal: 5,
  commitment: 6,
};

const LABEL_TO_DIMENSION: Record<string, string> = {
  "有無・事実": "fact",
  "数量": "quantity",
  "期間": "period",
  "頻度": "frequency",
  "方法・具体": "method_specific",
  "理由・背景": "reason_background",
  "工夫・調整": "adjustment",
  "工夫": "effort",
  "調整": "adjustment",
  "行動変容の意志確認": "behavior_intention",
  "行動変容・提案": "behavior_intention",
  "継続意志": "continuation_intention",
};

const LEVEL_DEFAULT_DIMENSION: Record<LevelName, string> = {
  Lv1: "fact",
  Lv2: "general",
  Lv3: "method_specific",
  Lv4: "reason_background",
  Lv5: "adjustment",
  Lv6: "behavior_intention",
};

const LEVEL_DEFAULT_LABEL: Record<LevelName, string> = {
  Lv1: "有無・事実",
  Lv2: "数量・期間・頻度",
  Lv3: "方法・具体",
  Lv4: "理由・背景",
  Lv5: "工夫・調整",
  Lv6: "行動変容の意志確認",
};

const DIMENSION_ALIASES: Record<string, string[]> = {
  fact: ["有無", "事実"],
  quantity: ["量", "数量", "何分", "どのくらい"],
  period: ["期間", "いつから", "何年前"],
  frequency: ["頻度", "何回", "週何回"],
  method_specific: ["方法", "具体", "やり方"],
  reason_background: ["理由", "背景", "原因"],
  effort: ["工夫"],
  adjustment: ["工夫", "調整"],
  behavior_intention: ["意志", "行動変容", "やってみたい"],
  continuation_intention: ["継続", "続ける"],
  general: [],
};

const DIMENSION_ORDER: Record<string, number> = {
  fact: 1,
  quantity: 2,
  period: 3,
  frequency: 4,
  method_specific: 5,
  reason_background: 6,
  effort: 7,
  adjustment: 8,
  behavior_intention: 9,
  continuation_intention: 10,
  general: 99,
};

export function normalizeConditionCategories(conditionData: unknown): NormalizedConditionCategory[] {
  if (!isRecord(conditionData)) return [];

  if (getNumber(conditionData.conditionTableVersion) === 3) {
    const fromV3 = normalizeFromCategoryDefinitionsV3(conditionData.categoryDefinitions);
    if (fromV3.length > 0) return fromV3;
  }

  const fromV2 = normalizeFromCategoryTableV2(conditionData.categoryTableV2);
  if (fromV2.length > 0) return fromV2;

  const fromCsv = normalizeFromCsvData(conditionData.csvData);
  if (fromCsv.length > 0) return fromCsv;

  return normalizeFromLegacyCategoryTable(conditionData.categoryTable);
}

export function buildLegacyCategoryTableFromNormalizedCategories(
  categories: NormalizedConditionCategory[],
): LegacyCategoryTable {
  const table: LegacyCategoryTable = {};

  for (const category of categories) {
    const row: Record<string, string> = {
      category: category.categoryName,
    };
    if (category.notes) row.notes = category.notes;

    const itemsByLevel = new Map<LevelKey, ConditionCategoryItem[]>();
    for (const item of category.items) {
      const list = itemsByLevel.get(item.level) ?? [];
      list.push(item);
      itemsByLevel.set(item.level, list);
    }

    const legacyLevels = Array.from(itemsByLevel.keys())
      .filter((level) => /^Lv\d+$/.test(level))
      .sort(compareLevelKeys);

    for (const level of legacyLevels) {
      const items = (itemsByLevel.get(level) ?? [])
        .slice()
        .sort(compareConditionItems)
        .filter((item) => item.text.trim().length > 0);
      if (items.length === 0) continue;

      row[level] = items
        .map((item) => {
          const needsLabel = items.length > 1 || item.columnKey.includes("_");
          return needsLabel ? `【${item.label}】${item.text}` : item.text;
        })
        .join("\n");
    }

    table[category.categoryId] = row;
  }

  return table;
}

export function getLegacyCategoryTable(conditionData: unknown): LegacyCategoryTable | null {
  if (!isRecord(conditionData)) return null;

  const categories = normalizeConditionCategories(conditionData);
  if (categories.length > 0) {
    return buildLegacyCategoryTableFromNormalizedCategories(categories);
  }

  return null;
}

export function compareConditionItems(left: ConditionCategoryItem, right: ConditionCategoryItem): number {
  const leftKindOrder = left.kind ? KIND_SORT_ORDER[left.kind] : 99;
  const rightKindOrder = right.kind ? KIND_SORT_ORDER[right.kind] : 99;
  const kindDiff = leftKindOrder - rightKindOrder;
  if (kindDiff !== 0) return kindDiff;
  const levelOrderDiff = (left.levelOrder ?? levelIndex(left.level)) - (right.levelOrder ?? levelIndex(right.level));
  if (levelOrderDiff !== 0) return levelOrderDiff;
  const levelDiff = compareLevelKeys(left.level, right.level);
  if (levelDiff !== 0) return levelDiff;
  if (left.order !== right.order) return left.order - right.order;
  return (DIMENSION_ORDER[left.dimension] ?? DIMENSION_ORDER.general) -
    (DIMENSION_ORDER[right.dimension] ?? DIMENSION_ORDER.general);
}

function normalizeFromCategoryTableV2(raw: unknown): NormalizedConditionCategory[] {
  if (!isRecord(raw)) return [];

  return sortCategoryIds(Object.keys(raw))
    .filter((categoryId) => /^C\d{1,2}$/.test(categoryId))
    .map((categoryId): NormalizedConditionCategory | null => {
      const rawCategory = raw[categoryId];
      if (!isRecord(rawCategory)) return null;

      const categoryName = getString(rawCategory.category) || categoryId;
      const rawItems = Array.isArray(rawCategory.items) ? rawCategory.items : [];
      const items = rawItems
        .map((rawItem, index) => normalizeRawV2Item(categoryId, categoryName, rawItem, index))
        .filter((item): item is ConditionCategoryItem => item !== null)
        .sort(compareConditionItems);

      return items.length > 0
        ? {
          categoryId,
          categoryName,
          items,
          notes: getString(rawCategory.notes) || undefined,
        }
        : null;
    })
    .filter((category): category is NormalizedConditionCategory => category !== null);
}

function normalizeFromCategoryDefinitionsV3(raw: unknown): NormalizedConditionCategory[] {
  if (!Array.isArray(raw)) return [];

  return raw
    .filter(isRecord)
    .slice()
    .sort((left, right) => {
      const orderDiff = (getNumber(left.order) ?? 0) - (getNumber(right.order) ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return getString(left.categoryId).localeCompare(getString(right.categoryId));
    })
    .map((category) => normalizeRawV3Category(category))
    .filter((category): category is NormalizedConditionCategory => category !== null);
}

function normalizeRawV3Category(rawCategory: RawRecord): NormalizedConditionCategory | null {
  const categoryId = getString(rawCategory.categoryId);
  if (!categoryId) return null;

  const categoryName = getString(rawCategory.displayName) || categoryId;
  const rawLevels = Array.isArray(rawCategory.levels) ? rawCategory.levels.filter(isRecord) : [];
  const levelMap = new Map<string, { level: LevelKey; kind: LevelKind; order: number; label: string }>();

  for (const rawLevel of rawLevels) {
    const levelKey = getString(rawLevel.levelKey);
    const kind = parseLevelKind(getString(rawLevel.kind));
    if (!levelKey || !kind) continue;
    levelMap.set(levelKey, {
      level: levelKey,
      kind,
      order: getNumber(rawLevel.order) ?? levelIndex(levelKey),
      label: getString(rawLevel.label) || levelKey,
    });
  }

  const rawItems = Array.isArray(rawCategory.items) ? rawCategory.items.filter(isRecord) : [];
  const siblingCounts = new Map<string, number>();
  for (const rawItem of rawItems) {
    const levelKey = getString(rawItem.levelKey);
    const levelInfo = levelMap.get(levelKey);
    if (!levelInfo) continue;
    const kind = parseLevelKind(getString(rawItem.kind)) ?? levelInfo.kind;
    const countKey = `${levelKey}:${kind}`;
    siblingCounts.set(countKey, (siblingCounts.get(countKey) ?? 0) + 1);
  }

  const items = rawItems
    .map((rawItem, index) => normalizeRawV3Item(categoryId, categoryName, rawItem, index, levelMap, siblingCounts))
    .filter((item): item is ConditionCategoryItem => item !== null)
    .sort(compareConditionItems);

  return items.length > 0
    ? {
      categoryId,
      categoryName,
      items,
      notes: getString(rawCategory.notes) || undefined,
    }
    : null;
}

function normalizeRawV3Item(
  categoryId: string,
  categoryName: string,
  rawItem: RawRecord,
  index: number,
  levelMap: Map<string, { level: LevelKey; kind: LevelKind; order: number; label: string }>,
  siblingCounts: Map<string, number>,
): ConditionCategoryItem | null {
  const levelKey = getString(rawItem.levelKey);
  const levelInfo = levelMap.get(levelKey);
  if (!levelInfo) return null;

  const kind = parseLevelKind(getString(rawItem.kind)) ?? levelInfo.kind;
  const sourceHeader = getString(rawItem.sourceHeader) || getString(rawItem.columnKey) || levelKey;
  const parsedHeader = parseLevelHeader(sourceHeader);
  const label = getString(rawItem.label) || parsedHeader?.label || levelInfo.label || defaultLabelForLevel(levelInfo.level);
  const dimension = getString(rawItem.dimension) || parsedHeader?.dimension || dimensionFromLabel(label, levelInfo.level, kind);
  const text = getString(rawItem.text);
  if (!text) return null;

  const siblingCount = siblingCounts.get(`${levelKey}:${kind}`) ?? 1;
  return buildConditionItem({
    categoryId,
    categoryName,
    level: levelInfo.level,
    kind,
    sourceHeader,
    columnKey: getString(rawItem.columnKey) || sourceHeader,
    dimension,
    label,
    text,
    order: getNumber(rawItem.order) ?? index,
    levelOrder: levelInfo.order,
    required: getBoolean(rawItem.required) ?? true,
    weight: getNumber(rawItem.weight) ?? 1,
    scoringPolicy: parseScoringPolicy(getString(rawItem.scoringPolicy)) ?? defaultScoringPolicyForKind(kind, siblingCount),
    aliases: Array.isArray(rawItem.aliases)
      ? rawItem.aliases.filter((value): value is string => typeof value === "string")
      : aliasesForDimension(dimension),
    reviewRequired: getBoolean(rawItem.reviewRequired) ?? false,
    groupKey: getString(rawItem.groupKey) || undefined,
    pairKey: getString(rawItem.pairKey) || undefined,
    pairedItemKey: getString(rawItem.pairedItemKey) || undefined,
    itemKey: getString(rawItem.itemKey) || undefined,
  });
}

function normalizeFromCsvData(raw: unknown): NormalizedConditionCategory[] {
  if (!isRecord(raw)) return [];
  const headers = Array.isArray(raw.headers) ? raw.headers.filter((value): value is string => typeof value === "string") : [];
  const rows = Array.isArray(raw.rows) ? raw.rows.filter(isRecord) : [];
  if (headers.length === 0 || rows.length === 0) return [];

  const levelHeaders = headers
    .map((header, index) => ({ header, index, parsed: parseLevelHeader(header) }))
    .filter((entry): entry is { header: string; index: number; parsed: ParsedLevelHeader } => entry.parsed !== null);

  if (levelHeaders.length === 0) return [];

  return rows
    .map((row): NormalizedConditionCategory | null => {
      const categoryId = getRowValue(row, ["id", "ID"]);
      if (!/^C\d{1,2}$/.test(categoryId)) return null;

      const categoryName = getRowValue(row, ["category", "カテゴリ"]) || categoryId;
      const items = levelHeaders
        .map(({ header, index, parsed }) => {
          const text = getString(row[header]);
          if (!text) return null;
          return buildConditionItem({
            categoryId,
            categoryName,
            level: parsed.level,
            sourceHeader: header,
            columnKey: header,
            dimension: parsed.dimension,
            label: parsed.label,
            text,
            order: index,
          });
        })
        .filter((item): item is ConditionCategoryItem => item !== null)
        .sort(compareConditionItems);

      return items.length > 0
        ? {
          categoryId,
          categoryName,
          items,
          notes: getRowValue(row, ["notes", "備考"]) || undefined,
        }
        : null;
    })
    .filter((category): category is NormalizedConditionCategory => category !== null)
    .sort((left, right) => sortCategoryIds([left.categoryId, right.categoryId]).indexOf(left.categoryId) === 0 ? -1 : 1);
}

function normalizeFromLegacyCategoryTable(raw: unknown): NormalizedConditionCategory[] {
  if (!isRecord(raw)) return [];

  return sortCategoryIds(Object.keys(raw))
    .filter((categoryId) => /^C\d{1,2}$/.test(categoryId))
    .map((categoryId): NormalizedConditionCategory | null => {
      const rawCategory = raw[categoryId];
      if (!isRecord(rawCategory)) return null;

      const categoryName = getString(rawCategory.category) || categoryId;
      const items = LEVEL_NAMES
        .map((level, index) => {
          const text = getString(rawCategory[level]);
          if (!text) return null;
          return buildConditionItem({
            categoryId,
            categoryName,
            level,
            sourceHeader: level,
            columnKey: level,
            dimension: LEVEL_DEFAULT_DIMENSION[level],
            label: LEVEL_DEFAULT_LABEL[level],
            text,
            order: index + 1,
          });
        })
        .filter((item): item is ConditionCategoryItem => item !== null);

      return items.length > 0
        ? {
          categoryId,
          categoryName,
          items,
          notes: getString(rawCategory.notes) || undefined,
        }
        : null;
    })
    .filter((category): category is NormalizedConditionCategory => category !== null);
}

function normalizeRawV2Item(
  categoryId: string,
  categoryName: string,
  rawItem: unknown,
  index: number,
): ConditionCategoryItem | null {
  if (!isRecord(rawItem)) return null;

  const level = parseLevelKey(getString(rawItem.level));
  if (!level) return null;

  const sourceHeader = getString(rawItem.sourceHeader) || getString(rawItem.columnKey) || level;
  const parsedHeader = parseLevelHeader(sourceHeader);
  const kind = parseLevelKind(getString(rawItem.kind));
  const label = getString(rawItem.label) || parsedHeader?.label || defaultLabelForLevel(level);
  const dimension = getString(rawItem.dimension) || parsedHeader?.dimension || dimensionFromLabel(label, level, kind);
  const text = getString(rawItem.text);
  if (!text) return null;

  return buildConditionItem({
    categoryId,
    categoryName,
    level,
    kind: kind ?? undefined,
    sourceHeader,
    columnKey: getString(rawItem.columnKey) || sourceHeader,
    dimension,
    label,
    text,
    order: getNumber(rawItem.order) ?? index + 1,
    required: getBoolean(rawItem.required) ?? true,
    weight: getNumber(rawItem.weight) ?? 1,
    scoringPolicy: parseScoringPolicy(getString(rawItem.scoringPolicy))
      ?? (kind ? defaultScoringPolicyForKind(kind, 1) : defaultScoringPolicy(level)),
    aliases: Array.isArray(rawItem.aliases)
      ? rawItem.aliases.filter((value): value is string => typeof value === "string")
      : aliasesForDimension(dimension),
    reviewRequired: getBoolean(rawItem.reviewRequired) ?? false,
    groupKey: getString(rawItem.groupKey) || undefined,
    pairKey: getString(rawItem.pairKey) || undefined,
    pairedItemKey: getString(rawItem.pairedItemKey) || undefined,
    itemKey: getString(rawItem.itemKey) || undefined,
  });
}

interface BuildItemOptions {
  categoryId: string;
  categoryName: string;
  level: LevelKey;
  kind?: LevelKind;
  sourceHeader: string;
  columnKey: string;
  dimension: string;
  label: string;
  text: string;
  order: number;
  required?: boolean;
  weight?: number;
  scoringPolicy?: ScoringPolicy;
  aliases?: string[];
  reviewRequired?: boolean;
  groupKey?: string;
  pairKey?: string;
  pairedItemKey?: string;
  itemKey?: string;
  levelOrder?: number;
}

function buildConditionItem(options: BuildItemOptions): ConditionCategoryItem {
  const scoringPolicy = options.scoringPolicy ?? defaultScoringPolicy(options.level);
  return {
    categoryId: options.categoryId,
    categoryName: options.categoryName,
    level: options.level,
    kind: options.kind,
    itemKey: options.itemKey ?? `${options.categoryId}_${options.level}_${options.dimension}`,
    columnKey: options.columnKey,
    sourceHeader: options.sourceHeader,
    dimension: options.dimension,
    label: options.label,
    text: options.text,
    order: options.order,
    required: options.required ?? true,
    weight: options.weight ?? 1,
    scoringPolicy,
    aliases: options.aliases ?? aliasesForDimension(options.dimension),
    reviewRequired: options.reviewRequired ?? false,
    groupKey: options.groupKey ?? defaultGroupKey(options.categoryId, options.level, scoringPolicy),
    pairKey: options.pairKey,
    pairedItemKey: options.pairedItemKey,
    levelOrder: options.levelOrder,
  };
}

interface ParsedLevelHeader {
  level: LevelKey;
  label: string;
  dimension: string;
}

function parseLevelHeader(header: string): ParsedLevelHeader | null {
  const match = LEVEL_HEADER_RE.exec(header.trim());
  if (!match) return null;

  const level = parseLevelKey(match[1]);
  if (!level) return null;

  const label = (match[2] ?? defaultLabelForLevel(level)).trim();
  return {
    level,
    label,
    dimension: dimensionFromLabel(label, level),
  };
}

function dimensionFromLabel(label: string, level: LevelKey, kind?: LevelKind | null): string {
  return LABEL_TO_DIMENSION[label] ?? defaultDimensionForLevel(level, kind);
}

function defaultScoringPolicy(level: LevelKey): ScoringPolicy {
  if (level === "Lv2") return "perItem";
  if (level === "Lv5" || level === "Lv6") return "anyOf";
  return "single";
}

function defaultScoringPolicyForKind(kind: LevelKind, siblingItemCount: number): ScoringPolicy {
  if (kind === "goal" || kind === "commitment") return "anyOf";
  if (kind === "measurement") return "perItem";
  return siblingItemCount > 1 ? "perItem" : "single";
}

function defaultGroupKey(categoryId: string, level: LevelKey, scoringPolicy: ScoringPolicy): string | undefined {
  return scoringPolicy === "anyOf" ? `${categoryId}_${level}` : undefined;
}

function aliasesForDimension(dimension: string): string[] {
  return DIMENSION_ALIASES[dimension] ?? [];
}

function levelIndex(level: LevelKey): number {
  const legacyIndex = (LEVEL_NAMES as readonly string[]).indexOf(level);
  if (legacyIndex >= 0) return legacyIndex;
  const numeric = /^Lv(\d+)$/.exec(level);
  return numeric ? Number(numeric[1]) - 1 : 999;
}

function parseLevelKey(value: string): LevelKey | null {
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function isLegacyLevelName(level: LevelKey): level is LevelName {
  return (LEVEL_NAMES as readonly string[]).includes(level);
}

function defaultDimensionForLevel(level: LevelKey, kind?: LevelKind | null): string {
  if (isLegacyLevelName(level)) return LEVEL_DEFAULT_DIMENSION[level];
  if (kind === "goal") return "goal";
  if (kind === "commitment") return "commitment";
  return "general";
}

function defaultLabelForLevel(level: LevelKey): string {
  return isLegacyLevelName(level) ? LEVEL_DEFAULT_LABEL[level] : level;
}

function parseScoringPolicy(value: string): ScoringPolicy | null {
  return value === "single" || value === "perItem" || value === "anyOf" ? value : null;
}

function parseLevelKind(value: string): LevelKind | null {
  return value === "fact" ||
    value === "measurement" ||
    value === "method" ||
    value === "reason" ||
    value === "goal" ||
    value === "commitment"
    ? value
    : null;
}

function getRowValue(row: RawRecord, keys: string[]): string {
  for (const key of keys) {
    const value = getString(row[key]);
    if (value) return value;
  }
  return "";
}

function getString(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

function getNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function getBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function isRecord(value: unknown): value is RawRecord {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function sortCategoryIds(ids: string[]): string[] {
  return ids.sort(compareCategoryIds);
}

function compareCategoryIds(left: string, right: string): number {
  const leftMatch = /^C(\d+)$/.exec(left);
  const rightMatch = /^C(\d+)$/.exec(right);
  if (leftMatch && rightMatch) return Number(leftMatch[1]) - Number(rightMatch[1]);
  if (leftMatch) return -1;
  if (rightMatch) return 1;
  return left.localeCompare(right, "ja");
}

function compareLevelKeys(left: LevelKey, right: LevelKey): number {
  const leftMatch = /^Lv(\d+)$/.exec(left);
  const rightMatch = /^Lv(\d+)$/.exec(right);
  if (leftMatch && rightMatch) return Number(leftMatch[1]) - Number(rightMatch[1]);
  if (leftMatch) return -1;
  if (rightMatch) return 1;
  return left.localeCompare(right, "ja");
}
